import { useCallback } from 'react'

import { getListingPagePaths } from 'Utils/getListingPagePaths'
import { sanitizePath } from 'Utils/sanitizePath'

const localePrefixes = ['de', 'es', 'fr', 'pt', 'ru', 'ja', 'zh-cn']

// Bridges nav category/subcategory clicks to the ListingPage. When the viewer is already on
// a 3D models / 3D print listing and clicks a category link for the same section, it cancels
// the full navigation and dispatches `listing-page:navigate-category` so the ListingPage's
// useCategoryNavigation can switch categories in place, preserving active filters. Off a
// listing page (or for a non-category link) it does nothing and the link navigates normally.
// Extracted from the legacy AssetItem so the redesigned CategoryMegaMenu shares one bridge.
function useListingCategoryNavigation({ isMobile = false } = {}) {
  return useCallback((event, destinationPath) => {
    if (!destinationPath) {
      return
    }

    // Sanitize the destination path (decode URL encoding and convert Unicode to ASCII)
    const sanitizedDestinationPath = sanitizePath(destinationPath)

    const currentPath = window.location.pathname
    const pathSegments = currentPath.split('/').filter(Boolean)
    const firstSegment = pathSegments[0]?.toLowerCase()

    const hasLocalePrefix = firstSegment && localePrefixes.includes(firstSegment)

    let isOn3dModelsCategoryPage = false
    let isOn3dPrintModelsCategoryPage = false
    const itemPageUmbrellaElement = document.querySelector('#listing-page-elements')
    const defaultPaths = getListingPagePaths(null)
    const localizedPaths = hasLocalePrefix ? getListingPagePaths(firstSegment) : null

    if (firstSegment === defaultPaths.modelsPath && pathSegments[1]) {
      isOn3dModelsCategoryPage = true
    } else if (hasLocalePrefix) {
      if (localizedPaths && pathSegments[1] === localizedPaths.modelsPath && pathSegments[2]) {
        isOn3dModelsCategoryPage = true
      } else if (pathSegments[1] === defaultPaths.modelsPath && pathSegments[2]) {
        isOn3dModelsCategoryPage = true
      }
    }

    if (firstSegment === defaultPaths.printModelsPath && pathSegments[1]) {
      isOn3dPrintModelsCategoryPage = true
    } else if (hasLocalePrefix) {
      if (localizedPaths && pathSegments[1] === localizedPaths.printModelsPath && pathSegments[2]) {
        isOn3dPrintModelsCategoryPage = true
      } else if (pathSegments[1] === defaultPaths.printModelsPath && pathSegments[2]) {
        isOn3dPrintModelsCategoryPage = true
      }
    }

    if (!itemPageUmbrellaElement || (!isOn3dModelsCategoryPage && !isOn3dPrintModelsCategoryPage)) {
      return
    }

    const normalizedDestinationPath = sanitizedDestinationPath.startsWith('/')
      ? sanitizedDestinationPath
      : `/${sanitizedDestinationPath}`
    const destinationPathSegments = normalizedDestinationPath.split('/').filter(Boolean)
    const destinationPathFirstSegment = destinationPathSegments[0]?.toLowerCase()

    const destinationHasLocalePrefix =
      destinationPathFirstSegment && localePrefixes.includes(destinationPathFirstSegment)
    const destinationLocalizedPaths = destinationHasLocalePrefix
      ? getListingPagePaths(destinationPathFirstSegment)
      : null

    const is3dModelsCategoryLink =
      destinationPathFirstSegment === defaultPaths.modelsPath ||
      (destinationHasLocalePrefix &&
        ((destinationLocalizedPaths && destinationPathSegments[1] === destinationLocalizedPaths.modelsPath) ||
          destinationPathSegments[1] === defaultPaths.modelsPath))
    const is3dPrintModelsCategoryLink =
      destinationPathFirstSegment === defaultPaths.printModelsPath ||
      (destinationHasLocalePrefix &&
        ((destinationLocalizedPaths && destinationPathSegments[1] === destinationLocalizedPaths.printModelsPath) ||
          destinationPathSegments[1] === defaultPaths.printModelsPath))

    if (is3dModelsCategoryLink || is3dPrintModelsCategoryLink) {
      event.preventDefault()

      window.dispatchEvent(
        new CustomEvent('listing-page:navigate-category', {
          detail: { href: sanitizedDestinationPath },
        })
      )

      if (isMobile) {
        window.dispatchEvent(
          new CustomEvent('listing-page:navigate-category-mobile')
        )
      }
    }
  }, [isMobile])
}

export default useListingCategoryNavigation
